import type { AIUnifiedEventPayload } from '@atlaskit/editor-common/analytics/types/ai-unified-events';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';

import type { CommonStepAttributes } from '../../ui/utils/analytics/analyticsFlowTypes';
import type { NextAction, Session, UndoneSession } from './types';

type AiSessionId = string;
type TimeoutId = number;

export interface AnalyticsToDefer {
	aiSessionId: AiSessionId;
	/** Backend deferrals can coexist with a pending frontend deferral. */
	isBackendPersisted?: boolean;
	payload: AIUnifiedEventPayload;
	waitForSessionEnd: boolean;
}

export interface DeferredAnalyticsState {
	deferred: Record<AiSessionId, TimeoutId>;
	sessionState: 'notStarted' | 'inProgress' | 'justEnded';
	toCancel?: AiSessionId;
	/** Captured before the cancelled session is cleared. */
	toCancelExperienceName?: string;
	/** Selects a clean backend discard payload instead of frontend attributes. */
	toCancelIsBackendPersisted?: boolean;
	/** Outstanding per-session deferrals. */
	toDefer?: AnalyticsToDefer[];
	/** Session whose pending accept should be removed without emitting discard. */
	toDrop?: AiSessionId;
}

export type CommonAttributes = Partial<CommonStepAttributes>;

export type AIMonitorWatchmenPluginState = {
	commonAttributes: CommonAttributes;
	currentSession: Session | null;

	deferredAnalytics: DeferredAnalyticsState;

	lastUndoneSession: UndoneSession | null;

	pendingAnalytics: {
		aiGroupId: string | undefined;
		aiOutputChars: number;
		aiOutputWords: number;
		aiSessionId: string;
		experienceName: string | undefined;
		nextActionLatencyMs: number;
		nextActionRemovedAiChars: number | undefined;
		nextActionRemovedAiWords: number | undefined;
		nextActionTouchedAiRanges: number | undefined;
		nextActionType: NextAction;
	} | null;
};

export const aiMonitorWatchmenPluginKey: PluginKey<AIMonitorWatchmenPluginState> =
	new PluginKey<AIMonitorWatchmenPluginState>('aiMonitorWatchmen');
